/**
 * toast-premium.ts — Premium toast message templates for Profileia.
 * ============================================================================
 * SPEC-240 v3 (Sprint 129, 2026-09-15 — Founder directive: "mejora tambien
 * los toast o mensajes que le muestra el sistema a los usuarios para que
 * sean premium").
 *
 * Centralizes the most common toast messages with consistent premium
 * phrasing. Use these helpers instead of raw toast.success/error() to
 * ensure consistent UX voice across the app.
 *
 * Voice guidelines:
 *   - Friendly but professional (not childish, not robotic)
 *   - First person when addressing the user ("Tú"/"Tu")
 *   - Specific about what happened + what to do next
 *   - Spanish (Latin American, informal "tú")
 *   - Premium feel: elegant, concise, confident
 *   - Avoid technical jargon ("catch block", "500 Internal Server Error")
 *   - Avoid robotic messages ("Operation successful", "Error occurred")
 *
 * Usage:
 *   import { toastSuccess, toastError, toastInfo, toastWarning } from '@/lib/toast-premium';
 *   toastSuccess.profileUpdated();
 *   toastError.network();
 *
 * Anti-§6 (CM-19/CM-15): NO business logic — pure UI helper.
 * ============================================================================
 */

import { toast } from 'sonner';

// ─── Success toasts (premium feel) ────────────────────────────────────────

export const toastSuccess = {
  /** Avatar / profile photo updated */
  avatarUpdated: () =>
    toast.success('Foto actualizada', { description: 'Tu nuevo look se ve increíble.' }),

  /** Cover image updated */
  coverUpdated: () =>
    toast.success('Portada actualizada', { description: 'Tu perfil tiene un aire renovado.' }),

  /** Profile saved (any field) */
  profileSaved: () =>
    toast.success('Perfil guardado', { description: 'Tus cambios están listos.' }),

  /** Settings saved */
  settingsSaved: () =>
    toast.success('Configuración guardada', { description: 'Todo quedó como lo pediste.' }),

  /** Wallet address updated */
  walletUpdated: () =>
    toast.success('Billetera actualizada', { description: 'Ya podés recibir tus comisiones ahí.' }),

  /** 2FA enabled */
  twoFactorEnabled: () =>
    toast.success('Doble factor activado', { description: 'Tu cuenta está aún más protegida.' }),

  /** 2FA disabled */
  twoFactorDisabled: () =>
    toast.success('Doble factor desactivado', { description: 'Podés reactivarlo cuando quieras.' }),

  /** Password changed */
  passwordChanged: () =>
    toast.success('Contraseña cambiada', { description: 'Inicia sesión con tu nueva clave.' }),

  /** Email verified */
  emailVerified: () =>
    toast.success('Email verificado', { description: 'Tu cuenta está confirmada.' }),

  /** Testimonial submitted */
  testimonialSubmitted: () =>
    toast.success('Testimonio enviado', { description: 'Lo revisaremos y lo publicaremos pronto.' }),

  /** Testimonial deleted */
  testimonialDeleted: () =>
    toast.success('Testimonio eliminado'),

  /** Link copied to clipboard */
  linkCopied: () =>
    toast.success('Enlace copiado', { description: 'Pegalo donde quieras compartirlo.', duration: 3000 }),

  /** Address copied */
  addressCopied: () =>
    toast.success('Dirección copiada', { duration: 2000 }),

  /** Withdrawal requested */
  withdrawalRequested: () =>
    toast.success('Retiro solicitado', { description: 'Te avisaremos cuando esté procesado.' }),

  /** Withdrawal processed (on-chain confirmed) */
  withdrawalProcessed: (network: string) =>
    toast.success('Retiro completado', { description: `Tu dinero ya está en la red ${network}.` }),

  /** Deposit confirmed */
  depositConfirmed: (amount: string) =>
    toast.success('Depósito confirmado', { description: `${amount} USDT ya están en tu billetera.` }),

  /** Package purchased */
  packagePurchased: (packageName: string) =>
    toast.success('¡Paquete activado!', { description: `Ya sos ${packageName}. Bienvenido al siguiente nivel.` }),

  /** Course enrolled */
  courseEnrolled: (courseName: string) =>
    toast.success('Inscripción confirmada', { description: `Ya podés empezar "${courseName}".` }),

  /** Module completed */
  moduleCompleted: () =>
    toast.success('Módulo completado', { description: '¡Vas por buen camino!' }),

  /** Generic save success */
  saved: () => toast.success('Guardado'),

  /** Generic create success */
  created: (what: string) => toast.success(`${what} creado`),

  /** Generic update success */
  updated: (what: string) => toast.success(`${what} actualizado`),

  /** Generic delete success */
  deleted: (what: string) => toast.success(`${what} eliminado`),
};

// ─── Error toasts (premium feel — clear, helpful, not alarming) ───────────

export const toastError = {
  /** Generic network error (lost connection, timeout) */
  network: () =>
    toast.error('Sin conexión', { description: 'Revisa tu internet e intenta de nuevo.' }),

  /** Generic unexpected error */
  unexpected: () =>
    toast.error('Algo salió mal', { description: 'Intenta de nuevo en unos segundos.' }),

  /** Auth required (session expired) */
  sessionExpired: () =>
    toast.error('Tu sesión expiró', { description: 'Inicia sesión de nuevo para continuar.' }),

  /** Permission denied */
  forbidden: () =>
    toast.error('No tienes permiso', { description: 'Esta acción no está disponible para tu cuenta.' }),

  /** Resource not found */
  notFound: (what: string) =>
    toast.error(`No encontramos ${what}`, { description: 'Puede que haya sido eliminado o movido.' }),

  /** Validation error (form) */
  validation: (field: string, reason: string) =>
    toast.error(`Revisa ${field}`, { description: reason }),

  /** Rate limited (too many attempts) */
  rateLimited: () =>
    toast.error('Demasiados intentos', { description: 'Espera unos minutos antes de intentar de nuevo.' }),

  /** Server error (5xx) */
  server: () =>
    toast.error('El servidor no responde', { description: 'Nuestro equipo ya fue notificado.' }),

  /** Wallet address invalid */
  invalidWallet: () =>
    toast.error('Dirección inválida', { description: 'Verifica que sea una wallet de USDT (BSC, Polygon o Arbitrum).' }),

  /** 2FA code invalid */
  invalid2fa: () =>
    toast.error('Código incorrecto', { description: 'Verifica el código de 6 dígitos e intenta de nuevo.' }),

  /** Insufficient balance */
  insufficientBalance: () =>
    toast.error('Saldo insuficiente', { description: 'Necesitás más fondos en tu billetera para esta operación.' }),

  /** KYC required */
  kycRequired: () =>
    toast.error('Verificá tu identidad primero', { description: 'Necesitás completar el KYC para retirar fondos.' }),

  /** 2FA required */
  twoFactorRequired: () =>
    toast.error('Verificá tu identidad', { description: 'Necesitás tener 2FA activado para esta acción.' }),

  /** Image upload error — wrong format */
  imageFormat: () =>
    toast.error('Formato no soportado', { description: 'Solo aceptamos JPG, PNG, WebP o AVIF.' }),

  /** Image upload error — too large */
  imageTooLarge: (maxMB: number = 2) =>
    toast.error('Imagen muy pesada', { description: `El máximo es ${maxMB}MB. Comprimela e intenta de nuevo.` }),

  /** Failed to save */
  saveFailed: (what?: string) =>
    toast.error('No se pudo guardar', { description: what ? `Revisa ${what} e intenta de nuevo.` : 'Intenta de nuevo en unos segundos.' }),

  /** Failed to load */
  loadFailed: (what: string) =>
    toast.error(`No pudimos cargar ${what}`, { description: 'Intenta recargar la página.' }),

  /** Generic action failed */
  actionFailed: (action: string) =>
    toast.error(`No se pudo ${action}`, { description: 'Intenta de nuevo en unos segundos.' }),
};

// ─── Info / warning toasts ───────────────────────────────────────────────

export const toastInfo = {
  /** Feature coming soon */
  comingSoon: (feature?: string) =>
    toast.info('Próximamente', { description: feature ? `${feature} estará disponible pronto.` : 'Esta función estará disponible pronto.' }),

  /** Copied to clipboard (info, not success) */
  copied: (what: string) =>
    toast.info(`${what} copiado`, { duration: 2000 }),

  /** Action in progress */
  processing: (what: string) =>
    toast.loading(`Procesando ${what}…`),
};

export const toastWarning = {
  /** Unsaved changes */
  unsavedChanges: () =>
    toast.warning('Tienes cambios sin guardar', { description: 'Guárdalos antes de salir de esta página.' }),

  /** Action is irreversible */
  irreversible: () =>
    toast.warning('Esta acción no se puede deshacer', { description: 'Confirma solo si estás seguro.' }),

  /** Maintenance mode */
  maintenance: () =>
    toast.warning('Mantenimiento programado', { description: 'Algunas funciones pueden no estar disponibles.' }),
};

// ─── Convenience: dismiss active toasts ──────────────────────────────────

export const dismissToast = toast.dismiss;

// Re-export sonner for cases where a custom toast is needed
export { toast };
